import React, { useMemo, useRef } from 'react';

import { cn } from '@/utils';

import { tokenizeEvidenceComposerText } from '../evidenceComposer';

interface QcEvidenceComposerInputProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className' | 'value'
> {
  className?: string;
  textareaClassName?: string;
  value: string;
}

export const QcEvidenceComposerInput = React.forwardRef<
  HTMLTextAreaElement,
  QcEvidenceComposerInputProps
>(({ className, onScroll, placeholder, textareaClassName, value, ...props }, forwardedRef) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const highlightContentRef = useRef<HTMLDivElement | null>(null);
  const parts = useMemo(() => tokenizeEvidenceComposerText(value), [value]);
  const inputLayerClassName = cn(
    'min-h-[60px] w-full px-3 py-2 text-base md:text-sm',
    textareaClassName,
  );

  const setTextareaRef = (node: HTMLTextAreaElement | null) => {
    textareaRef.current = node;

    if (!forwardedRef) {
      return;
    }

    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
      return;
    }

    forwardedRef.current = node;
  };

  const syncHighlightScroll = (target: HTMLTextAreaElement) => {
    if (!highlightContentRef.current) {
      return;
    }

    highlightContentRef.current.style.transform = `translate(${-target.scrollLeft}px, ${-target.scrollTop}px)`;
  };

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md border border-input bg-background shadow-xs focus-within:ring-1 focus-within:ring-ring',
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-md"
      >
        {value ? (
          <div
            ref={highlightContentRef}
            className={cn('whitespace-pre-wrap break-words', inputLayerClassName)}
          >
            {parts.map((part, index) =>
              part.type === 'timestamp' ? (
                <span key={`${part.text}-${index}`} className="font-medium text-primary">
                  {part.text}
                </span>
              ) : (
                <span key={`${part.text}-${index}`} className="text-foreground">
                  {part.text}
                </span>
              ),
            )}
            {value.endsWith('\n') ? '\n' : null}
          </div>
        ) : (
          <div className={cn('text-muted-foreground', inputLayerClassName)}>{placeholder}</div>
        )}
      </div>
      <textarea
        {...props}
        ref={setTextareaRef}
        value={value}
        placeholder=""
        className={cn(
          'relative z-10 flex resize-y bg-transparent text-transparent caret-foreground focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          '[text-shadow:0_0_0_transparent] selection:bg-primary/15',
          inputLayerClassName,
        )}
        onScroll={event => {
          syncHighlightScroll(event.currentTarget);
          onScroll?.(event);
        }}
      />
    </div>
  );
});

QcEvidenceComposerInput.displayName = 'QcEvidenceComposerInput';
